import { MultipleChoiceOption } from '../components/widgets/MultipleChoiceWidget'

export type WidgetType =
  | 'multipleChoice'
  | 'datePicker'
  | 'priority'
  | 'textInput'
  | 'checklist'

export interface ConversationStep {
  id: string
  type: WidgetType
  question: string
  options?: MultipleChoiceOption[]
  placeholder?: string
  optional?: boolean
  dataKey: string // Key to store the response
}

export interface ConversationFlow {
  id: string
  trigger: string[] // Keywords that trigger this flow
  category: string
  steps: ConversationStep[]
  templateId: string // Links to checklist template
}

// Predefined conversation flows
export const conversationFlows: ConversationFlow[] = [
  {
    id: 'school-enrollment',
    trigger: ['iscrivere', 'iscrizione', 'scuola', 'asilo'],
    category: 'Scuola',
    templateId: 'school-enrollment',
    steps: [
      {
        id: 'deadline',
        type: 'datePicker',
        question: "Entro quando devi completare l'iscrizione?",
        dataKey: 'deadline',
      },
      {
        id: 'priority',
        type: 'priority',
        question: 'Quanto è urgente?',
        dataKey: 'priority',
      },
      {
        id: 'notes',
        type: 'textInput',
        question: 'Hai note o dettagli aggiuntivi?',
        placeholder: 'Es. preferenze, richieste speciali...',
        optional: true,
        dataKey: 'notes',
      },
    ],
  },
  {
    id: 'doctor-change',
    trigger: ['pediatra', 'medico', 'cambiare', 'dottore'],
    category: 'Salute',
    templateId: 'doctor-change',
    steps: [
      {
        id: 'reason',
        type: 'multipleChoice',
        question: 'Perché vuoi cambiare pediatra?',
        options: [
          { id: 'moved', label: 'Trasferimento' },
          { id: 'unsatisfied', label: 'Insoddisfazione' },
          { id: 'other', label: 'Altro motivo' },
        ],
        dataKey: 'reason',
      },
      {
        id: 'deadline',
        type: 'datePicker',
        question: 'Entro quando vuoi completare il cambio?',
        dataKey: 'deadline',
      },
      {
        id: 'priority',
        type: 'priority',
        question: 'Quanto è urgente?',
        dataKey: 'priority',
      },
    ],
  },
  {
    id: 'passport-renewal',
    trigger: ['passaporto', 'rinnovare', 'documento'],
    category: 'Burocrazia',
    templateId: 'passport-renewal',
    steps: [
      {
        id: 'deadline',
        type: 'datePicker',
        question: 'Entro quando ti serve il passaporto?',
        dataKey: 'deadline',
      },
      {
        id: 'priority',
        type: 'priority',
        question: 'Quanto è urgente?',
        dataKey: 'priority',
      },
      {
        id: 'notes',
        type: 'textInput',
        question: 'Hai già alcuni documenti pronti?',
        placeholder: 'Es. foto, documenti identità...',
        optional: true,
        dataKey: 'notes',
      },
    ],
  },
  {
    id: 'sports-enrollment',
    trigger: ['sport', 'calcio', 'nuoto', 'palestra', 'attività'],
    category: 'Sport',
    templateId: 'sports-enrollment',
    steps: [
      {
        id: 'sport',
        type: 'textInput',
        question: 'Quale sport o attività?',
        placeholder: 'Es. calcio, nuoto, danza...',
        dataKey: 'sportName',
      },
      {
        id: 'deadline',
        type: 'datePicker',
        question: 'Entro quando vuoi iscriverlo/a?',
        dataKey: 'deadline',
      },
      {
        id: 'priority',
        type: 'priority',
        question: 'Quanto è urgente?',
        dataKey: 'priority',
      },
    ],
  },
]

// Match user input to a conversation flow
export function matchFlow(userInput: string): ConversationFlow | null {
  const input = userInput.toLowerCase()

  for (const flow of conversationFlows) {
    if (flow.trigger.some((keyword) => input.includes(keyword))) {
      return flow
    }
  }

  return null
}
